import { useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type Analysis = {
  id: string;
  projectName: string;
  status: string;
  createdAt: string;
};

/**
 * Admin org detail — lists analyses for a target org via the bypass-RLS admin API.
 * Route: /admin/orgs/:orgId
 */
export function AdminOrgRoute() {
  const { orgId } = useParams<{ orgId: string }>();
  const getToken = useSessionTokenGetter();
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<
    | {
        ok: boolean;
        areaName: string;
        results: { pipeline: string; recordsInserted?: number; error?: string }[];
      }
    | { error: string }
    | null
  >(null);

  const fetchAnalyses = useCallback(async () => {
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/admin/orgs/${orgId}/analyses`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error((j as { error?: string }).error ?? `${res.status}`);
    }
    return res.json() as Promise<{ count: number; analyses: Analysis[] }>;
  }, [getToken, orgId]);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchAnalyses();
        if (!cancelled) setAnalyses(data.analyses);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchAnalyses, orgId]);

  const STATUS_COLOR: Record<string, string> = {
    draft: 'bg-slate-100 text-slate-600',
    in_review: 'bg-yellow-50 text-yellow-700',
    approved: 'bg-green-50 text-green-700',
    presented: 'bg-blue-50 text-blue-700',
    archived: 'bg-slate-50 text-slate-400',
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <div className="mb-6">
        <Link to="/admin" className="text-xs text-slate-400 hover:underline">
          ← All organizations
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">Org: {orgId}</h1>
        <p className="mt-1 text-sm text-slate-500">
          Read-only view. Access is audit-logged in this org's trail.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Link
            to={`/admin/orgs/${orgId}/billing`}
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            Billing → plan + members
          </Link>
          <Link
            to={`/admin/orgs/${orgId}/governance`}
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            Governance → profiles + authorizations
          </Link>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={seeding}
            onClick={async () => {
              setSeeding(true);
              setSeedResult(null);
              try {
                const token = await getToken();
                const res = await fetch(`${API}/api/v1/admin/orgs/${orgId}/seed-pipelines`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                });
                const j = await res.json();
                setSeedResult(j);
              } catch (e) {
                setSeedResult({ error: e instanceof Error ? e.message : String(e) });
              } finally {
                setSeeding(false);
              }
            }}
            className="inline-flex items-center rounded-md border border-develio-teal bg-develio-teal/10 px-3 py-1.5 text-xs font-medium text-develio-tealDark hover:bg-develio-teal/20 disabled:opacity-50"
          >
            {seeding ? 'Seeding…' : 'Seed Market Data'}
          </button>
          {seedResult && 'ok' in seedResult && (
            <span className="text-xs text-green-700">
              ✓ {seedResult.areaName}:{' '}
              {seedResult.results
                .map((r) =>
                  r.error
                    ? `${r.pipeline}(ERR: ${r.error})`
                    : `${r.pipeline}(${r.recordsInserted})`,
                )
                .join(', ')}
            </span>
          )}
          {seedResult && 'error' in seedResult && (
            <span className="text-xs text-red-600">{seedResult.error}</span>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {loading && <div className="py-12 text-center text-sm text-slate-500">Loading analyses…</div>}

      {!loading && analyses.length === 0 && !error && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-sm text-slate-500">No analyses in this organization.</p>
        </div>
      )}

      {!loading && analyses.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Project</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Status</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {analyses.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{a.projectName}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_COLOR[a.status] ?? 'bg-slate-100 text-slate-600'}`}
                    >
                      {a.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {new Date(a.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
